import 'element-closest';
import { removeIE } from '../../tools/helpers';
import { KEYCODES, CONFIG, ANIMATIONS } from '../config';
import initModules from '../initModules';
import trackPageView from '../WebTrekk';

const NAV_CURRENT_CLASS = 'tab-navigation-item--current';
const ENTRY_CURRENT_CLASS = 'tab-navigation-entry--current';
const NAV_SCROLL_X_START = 'tab-navigation__nav--scroll-x-start';
const NAV_SCROLL_X_END = 'tab-navigation__nav--scroll-x-end';
const NAV_TAB_CLASS = 'tab-navigation-item__wrapper';
const NAV_TAB_ICON_CLASS = 'tab-navigation__tab-icon';
const NAV_TAB_TITLE_CLASS = 'tab-navigation__tab-title';
const NAV_TAB_LINK_CLASS = 'tab-navigation__link';
const NAV_SHOW_SPINNER = 'tab-navigation__content-spinner--show';

class TabNavigation {

  constructor(options = {}) {
    this.$root = options.element;
    this.$tabsNav = this.$root.querySelector('.js-tab-navigation');
    this.$arrowLeft = this.$tabsNav.querySelector('.js-tab-navigation-arrow-left');
    this.$arrowRight = this.$tabsNav.querySelector('.js-tab-navigation-arrow-right');
    this.$tabsNavUlWrapper = this.$root.querySelector('.js-tab-navigation-ul-wrapper');
    this.$tabsEntries = this.$root.querySelectorAll('.js-tab-navigation-entry');
    this.$spinner = this.$root.querySelector('.js-tab-navigation-spinner');
    this.id = this.$root.id;
  }

  init() {
    this.buildNav();
    this.bindEvents();
    this.setCurrentTabActive();
  }

  /**
   * Function to bind all needed events
   * @function
   */
  bindEvents() {
    this.$tabsNav.querySelectorAll('li')
      .forEach(($navItem) => {
        $navItem.addEventListener('click', this.handleTabClick.bind(this));
      });

    window.addEventListener(CONFIG.events.RESIZE, () => this.handleResize());
    window.addEventListener('keydown', event => this.handleKeyDown(event));

    this.$tabsNavUlWrapper.addEventListener('scroll', (event) => {
      this.handleScrollingNav(event);
    });

    this.$arrowLeft.addEventListener('click', () => this.scrollToPreviousTab());
    this.$arrowRight.addEventListener('click', () => this.scrollToNextTab());
  }

  /**
   * helper for tab selection
   * @param id
   * @returns {HTMLLIElement | null}
   */
  getTabById(id) {
    return this.$root.querySelector(`[href="#${id}"]`)
      .closest('li');
  }

  // noinspection JSMethodCanBeStatic
  /**
   * helper for tab selection with page reload
   * @param $tab
   */
  getUrlByTab($tab) {
    return $tab.querySelector('a')
      .getAttribute('href');
  }

  /**
   * helper for tab selection
   * @param $tab
   * @returns {string}
   */
  getIdByTab($tab) {
    const $a = $tab.querySelector('a');
    return this.getHashString($a.href);
  }

  /**
   * helper for tab selection
   * @param id
   * @returns Element | SVGElementTagNameMap[string] | HTMLElementTagNameMap[string]
   */
  getContentEntryById(id) {
    return this.$root.querySelector(`#${id}`);
  }

  /**
   * helper for tab selection
   * @returns {string[]}
   */
  getTabIds() {
    return [...this.$root.querySelectorAll('.tab-navigation__ul li')]
      .map($li => $li.querySelector('a'))
      .map($el => this.getHashString($el.href));
  }

  /**
   * Function to handle horizontal scroll of tab navigation and fade out overflow
   * @function
   */
  handleScrollingNav(event) {
    const boundingUL = event.currentTarget.querySelector('ul')
      .getBoundingClientRect();
    const boundingNav = this.$tabsNav.getBoundingClientRect();

    if (this.$tabsNavUlWrapper.scrollLeft > 10) {
      this.$tabsNav.classList.remove(NAV_SCROLL_X_START);
      this.$tabsNav.classList.remove(NAV_SCROLL_X_END);

      if (boundingUL.right <= boundingNav.right + 10) {
        if (!this.$tabsNav.classList.contains(NAV_SCROLL_X_END)) {
          this.$tabsNav.classList.add(NAV_SCROLL_X_END);
        }
      }

    } else {
      this.$tabsNav.classList.add(NAV_SCROLL_X_START);
      this.$tabsNav.classList.remove(NAV_SCROLL_X_END);
    }
  }

  /**
   * compute position and scroll to next tab
   */
  scrollToNextTab() {
    const currentIndex = this.getCurrentLeftMostTab();
    const currentScrollLeft = this.$tabsNavUlWrapper.scrollLeft;
    // scroll to next tab
    const targetScrollLeft = Array.prototype.slice.call(this.$tabsNavLiElements)[currentIndex + 1].offsetLeft;

    this.smoothScrollTo(currentScrollLeft, targetScrollLeft);
  }

  /**
   * compute position and scroll to previous tab
   */
  scrollToPreviousTab() {
    const currentIndex = this.getCurrentLeftMostTab(true);
    const currentScrollLeft = this.$tabsNavUlWrapper.scrollLeft;
    let targetScrollLeft = 0;

    if (currentIndex > 0) {
      if (this.isTabLeftAligned(currentIndex)) {
        // scroll to previous tab, if we are at the beginning of a tab
        targetScrollLeft = Array.prototype.slice.call(this.$tabsNavLiElements)[currentIndex - 1].offsetLeft;
      } else {
        // scroll to beginning of current tab, if we are not there already
        targetScrollLeft = Array.prototype.slice.call(this.$tabsNavLiElements)[currentIndex].offsetLeft;
      }
    } // if currentIndex === 0, then targetScrollLeft stays 0

    this.smoothScrollTo(currentScrollLeft, targetScrollLeft);
  }

  /**
   * @param currentScrollLeft
   * @param targetScrollLeft
   * scroll tabsNavUlWrapper smoothly to a certain position by going in several steps
   */
  smoothScrollTo(currentScrollLeft, targetScrollLeft) {
    const steps = 10;
    const distance = targetScrollLeft - currentScrollLeft;
    let slideCount = 0;

    const slideTimer = setInterval(() => {
      slideCount += 1;
      // compute from initial value to avoid rounding mistakes
      this.$tabsNavUlWrapper.scrollLeft = currentScrollLeft + (distance / steps * slideCount);

      if (slideCount === steps) {
        window.clearInterval(slideTimer);
      }
    }, ANIMATIONS.default.DURATION * 1000 / steps);
  }

  /**
   * returns the index of the tab which is currently the most left one visible
   */
  getCurrentLeftMostTab() {
    return Array.prototype.slice.call(this.$tabsNavLiElements)
      .reduce((bestIndex, $current, index) => {
        if (this.$tabsNavUlWrapper.scrollLeft >= $current.offsetLeft) {
          return index;
        }
        return bestIndex;
      }, 0);
  }

  /**
   * @param index
   * @returns {boolean} - true, if selected tab is currentLeftMostTab and fully visible
   *    This matters for scrolling to previous tab, since otherwise we only want to jump to start of current tab
   */
  isTabLeftAligned(index) {
    return Array.prototype.slice.call(this.$tabsNavLiElements)[index].offsetLeft === this.$tabsNavUlWrapper.scrollLeft;
  }

  /**
   * helper for tab selection
   */
  deselectAllTabs() {
    const ids = this.getTabIds();

    ids.forEach((id) => {
      this.getTabById(id)
        .classList
        .remove(NAV_CURRENT_CLASS);
      this.getContentEntryById(id)
        .classList
        .remove(ENTRY_CURRENT_CLASS);
    });
  }

  /**
   * Function to handle arrow left and right key down
   * @param event
   */
  handleKeyDown(event) {
    const $currentTab = this.$root.querySelector(`.${NAV_CURRENT_CLASS}`);
    const key = event.keyCode || event.which;

    if (document.activeElement.nodeName === 'DIV') {
      event.preventDefault();
    } else if (document.activeElement.nodeName !== 'INPUT') {
      if (key === KEYCODES.LEFT) {
        const $previousTab = $currentTab.previousElementSibling;
        if ($previousTab) {
          $previousTab.querySelector(`.${NAV_TAB_LINK_CLASS}`)
            .focus();
          const previousTabId = this.getIdByTab($previousTab);
          if (previousTabId) {
            this.selectTab({ id: previousTabId });
          }
        }
      }

      if (key === KEYCODES.RIGHT) {
        const $nextTab = $currentTab.nextElementSibling;
        if ($nextTab) {
          $nextTab.querySelector(`.${NAV_TAB_LINK_CLASS}`)
            .focus();
          const nextTabId = this.getIdByTab($nextTab);
          this.selectTab({ id: nextTabId });
        }
      }
    }
  }

  /**
   * Function to handle tab click
   * @param event
   */
  handleTabClick(event) {
    const tabUrl = this.getUrlByTab(event.currentTarget);

    if (tabUrl[0] === '#') {
      event.preventDefault();

      const tabId = this.getIdByTab(event.currentTarget);
      this.selectTab({ id: tabId });
    }
  }

  /**
   * Function to handle visibility of tabs on click of item in tab navigation
   * @function
   */
  selectTab({ id }) {
    const $tab = this.getTabById(id);
    const $contentEntry = this.getContentEntryById(id);
    const $contentContainer = $contentEntry.querySelector('.tab-navigation-entry__content-inner');

    this.trackTabView($contentEntry);

    this.deselectAllTabs();

    $tab.classList.add(NAV_CURRENT_CLASS);

    if (!$contentContainer.innerHTML.trim() && $contentEntry.dataset.apiLoad) {
      $contentEntry.classList.add(ENTRY_CURRENT_CLASS);
      this.loadFromAPI(id);
    } else {
      $contentEntry.classList.add(ENTRY_CURRENT_CLASS);
      this.$spinner.classList.remove(NAV_SHOW_SPINNER);
    }
  }

  loadFromAPI(id) {
    const $contentEntry = this.getContentEntryById(id);
    const $contentContainer = $contentEntry.querySelector('.tab-navigation-entry__content-inner');

    if ($contentContainer.innerHTML.trim() === '' && $contentEntry.dataset.apiLoad) {
      this.$spinner.classList.add(NAV_SHOW_SPINNER);

      window.divae.restApi
        .loadData($contentEntry.dataset.apiLoad, 'application/xhtml+xml')
        .then((_response) => {
          $contentContainer.innerHTML = _response;
          initModules({ rootElement: $contentContainer });
          this.$spinner.classList.remove(NAV_SHOW_SPINNER);
          const $tableAd = $contentEntry.querySelector('.bfv-table__ad');
          const $adBanners = $contentEntry.querySelectorAll('.bfv-ad-banner');

          if ($tableAd && $adBanners) {
            let adClass = $adBanners[0].classList.item(1);
            let adId = $adBanners[0].id;

            if (window.matchMedia('(max-width: 767px)').matches) {
              adClass = $adBanners[1].classList.item(1);
              adId = $adBanners[1].id;
            }

            // eslint-disable-next-line no-console
            console.log(`Refresh Ad with ID: ${adId}`);
            // eslint-disable-next-line no-undef
            traffective.refreshSlot(adClass, adId);
          }
        });
    }
  }

  /**
   * build Tab List for Navigation
   */
  buildNav() {
    const templateNavItem = (text, id, isCurrent, listItemIcon, url) => {
      const nodeListItem = document.createElement('li');

      if (isCurrent) {
        nodeListItem.className = NAV_CURRENT_CLASS;
      }

      const href = url || `#${id}`;

      if (listItemIcon) {
        const listItemIconClone = listItemIcon.cloneNode(true);
        listItemIconClone.className = NAV_TAB_ICON_CLASS;
        removeIE(listItemIcon);
        const listItemIconHtml = listItemIconClone.outerHTML;

        nodeListItem.innerHTML = `<a class="${NAV_TAB_LINK_CLASS}" href="${href}" draggable="false" tabindex="-1"  title="${text}"><div class="${NAV_TAB_CLASS}">${listItemIconHtml}<span class="${NAV_TAB_TITLE_CLASS}">${text}</span></div></a>`;

      } else {
        nodeListItem.innerHTML = `<a class="${NAV_TAB_LINK_CLASS}" href="${href}" draggable="false" tabindex="-1" title="${text}"><span class="${NAV_TAB_TITLE_CLASS}">${text}</span></a>`;
      }

      nodeListItem.setAttribute('role', 'tab');
      return nodeListItem;
    };

    this.$tabsEntries.forEach(($entry) => {
      const $naviItem = templateNavItem(
        $entry.dataset.navItem,
        $entry.getAttribute('id'),
        $entry.classList.contains(ENTRY_CURRENT_CLASS),
        $entry.querySelector('.tab-navigation-entry__icon .icon'),
        $entry.dataset.url,
      );
      this.$tabsNav.querySelector('ul')
        .appendChild($naviItem);
    });

    // Add scrolled to end if does need to get scrolled
    if (this.$tabsNavUlWrapper.scrollWidth > Math.ceil(this.$tabsNavUlWrapper.getBoundingClientRect().width)) {
      this.$tabsNav.classList.remove(NAV_SCROLL_X_END);
    }

    this.$tabsNavLiElements = this.$tabsNavUlWrapper.querySelectorAll('li');
  }

  /**
   * Recomputed classes for arrows and shadows
   */
  handleResize() {
    // Add scrolled to end if does not need to get scrolled
    if (this.$tabsNavUlWrapper.scrollWidth <= Math.ceil(this.$tabsNavUlWrapper.getBoundingClientRect().width)) {
      this.$tabsNav.classList.add(NAV_SCROLL_X_END);
    } else {
      this.$tabsNav.classList.remove(NAV_SCROLL_X_END);
    }
  }

  // noinspection JSMethodCanBeStatic
  /**
   * A helper for tab changing
   * @param url
   * @returns {string}
   */
  getHashString(url = document.location.href) {
    return url.split('#')
      .slice(1)
      .join('#');
  }

  /**
   * If tab is loaded via page reload, we have to manually set the current tab.
   * However, we don't want to remove current tab, if not loaded via page reload.
   */
  setCurrentTabActive() {
    let $current = null;
    let $new = null;

    [...this.$root.querySelectorAll('.tab-navigation__ul li')].forEach(($li) => {
      // already existing current tab
      if ($li.classList.contains(NAV_CURRENT_CLASS)) {
        $current = $li;
      }

      // loaded via page reload
      if (document.location.href === $li.querySelector('a').href) {
        $new = $li;
      }
    });

    if ($new && $current !== $new) {
      if ($current) {
        $current.classList.remove(NAV_CURRENT_CLASS);
      }

      $new.classList.add(NAV_CURRENT_CLASS);
    }

    const currentId = this.getIdByTab($current);
    if (currentId !== '') {
      this.loadFromAPI(currentId);
      this.trackTabView(this.getContentEntryById(currentId));
    }
  }

  trackTabView($contentEntry) {
    if ($contentEntry && $contentEntry.dataset.trackingContentId) {
      const tabTrackingData = { contentId: $contentEntry.dataset.trackingContentId };
      if ($contentEntry.dataset.trackingContentGroup) {
        tabTrackingData.contentGroup = JSON.parse($contentEntry.dataset.trackingContentGroup);
      }
      if ($contentEntry.dataset.trackingCustomParameter && $contentEntry.dataset.trackingCustomParameter !== 'null') {
        tabTrackingData.customParameter = JSON.parse($contentEntry.dataset.trackingCustomParameter);
      }
      trackPageView(tabTrackingData);
    }
  }

}

export default TabNavigation;



// WEBPACK FOOTER //
// ./src/javascript/main/MD115_Reitermodul/index.js